import { BoostProvider, BoostTask } from '../../types/boost';
import { createRoot } from 'react-dom/client';
import AdWidgetModal from '../../../../Components/AdWidgetFromTads/TadsWidgetModal';
import { useNotificationStore } from '../../../../Store/NotificationStore';

export class TadsBoostProvider implements BoostProvider {
    private static instance: TadsBoostProvider;
    private hasError: boolean = false;
    private modalRoot: HTMLDivElement | null = null;
    private root: ReturnType<typeof createRoot> | null = null;
    private isTaskInProgress: boolean = false;
    private lastTaskTime: number = 0;
    private readonly TASK_COOLDOWN = 5000; // 5 секунд кулдауна между задачами

    private constructor() {}

    static getInstance(): TadsBoostProvider {
        if (!TadsBoostProvider.instance) {
            TadsBoostProvider.instance = new TadsBoostProvider();
        }
        return TadsBoostProvider.instance;
    }

    private cleanup() {
        if (this.root) {
            this.root.unmount();
            this.root = null;
        }
        
        if (this.modalRoot && document.body.contains(this.modalRoot)) {
            document.body.removeChild(this.modalRoot);
            this.modalRoot = null;
        }
        
        this.isTaskInProgress = false;
        this.hasError = false;
        this.lastTaskTime = 0;

        setTimeout(() => {
            window.postMessage({ eventType: 'tads_modal_closed' }, '*');
        }, 100);
    }

    async startTask(_taskId: string | number): Promise<'success' | 'user_cancelled' | 'ads_not_found'> {
        if (this.isTaskInProgress) {
            console.log('TadsBoostProvider: Cannot start task: task already in progress');
            this.cleanup();
            return Promise.resolve('ads_not_found');
        }

        const canShow = this.canShowNewTask();
        if (!canShow) {
            console.log('TadsBoostProvider: Cannot start task: on cooldown');
            const remainingCooldown = Math.ceil((this.TASK_COOLDOWN - (Date.now() - this.lastTaskTime)) / 1000);
            useNotificationStore.getState().setNotification({
                type: 'Warning',
                message: `Подождите ${remainingCooldown} сек. перед следующей попыткой.`
            });
            return Promise.resolve('ads_not_found');
        }

        this.isTaskInProgress = true;
        this.lastTaskTime = Date.now();
        this.hasError = false;

        try {
            console.log('TadsBoostProvider: Starting task...');
            
            // Показываем модалку сразу, без предварительной проверки
            return new Promise<'success' | 'user_cancelled' | 'ads_not_found'>((resolve) => {
                this.modalRoot = document.createElement('div');
                document.body.appendChild(this.modalRoot);
                this.root = createRoot(this.modalRoot);

                const handleReward = () => {
                    console.log('Tads task completed successfully');
                    this.cleanup();
                    resolve('success');
                };

                const handleClose = () => {
                    console.log('Tads task closed by user');
                    this.cleanup();
                    resolve('user_cancelled');
                };

                const handleAdsNotFound = () => {
                    console.log('Tads ads not found');
                    this.hasError = true;
                    this.cleanup();
                    resolve('ads_not_found');
                };

                this.root.render(
                    <AdWidgetModal
                        isOpen={true}
                        isClosing={false}
                        onClose={handleClose}
                        onReward={handleReward}
                        onAdsNotFound={handleAdsNotFound}
                        checkOnly={false}
                    />
                );
            });
        } catch (error) {
            console.error('Error in Tads task:', error);
            this.cleanup();
            return Promise.resolve('ads_not_found');
        }
    }

    private canShowNewTask(): boolean {
        const timeSinceLastTask = Date.now() - this.lastTaskTime;
        const isOnCooldown = timeSinceLastTask < this.TASK_COOLDOWN;
        
        console.log(`TadsBoostProvider: Time since last task: ${timeSinceLastTask}ms, Cooldown: ${this.TASK_COOLDOWN}ms, Is on cooldown: ${isOnCooldown}, Is task in progress: ${this.isTaskInProgress}`);
        
        return !isOnCooldown && !this.isTaskInProgress;
    }

    async getAvailableTasks(): Promise<BoostTask[]> {
        if (this.hasError) {
            console.log("TadsBoostProvider: Skipping task due to previous error.");
            return [];
        }

        const canShow = this.canShowNewTask();
        if (!canShow) {
            console.log("TadsBoostProvider: Skipping task due to active task or cooldown.");
            return [];
        }

        const task: BoostTask = {
            id: 'tads-task',
            title: 'Ускорить за рекламу (Tads)',
            provider: this.getName(),
            onClick: () => this.startTask('tads-task')
        };

        return [task];
    }

    getName(): string {
        return 'Tads';
    }

    resetError() {
        console.log("TadsBoostProvider: Resetting error state.");
        this.cleanup();
    }
}

export const tadsBoostProvider = TadsBoostProvider.getInstance();